import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import api from "../services/api";

const AuthContext = createContext();

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
};

export const AuthProvider = ({ children }) => {
  const [corporator, setCorporator] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionTimerRef = useRef(null);

  /* ================= LOGOUT ================= */

  const logout = useCallback(() => {
    clearTimeout(sessionTimerRef.current);

    localStorage.removeItem("corporator_token");

    setCorporator(null);

    window.location.href = "/login";
  }, []);

  /* ================= SESSION TIMER ================= */

  const resetSessionTimer = useCallback(() => {
    clearTimeout(sessionTimerRef.current);

    sessionTimerRef.current = setTimeout(() => {
      alert("Session expired due to inactivity.");
      logout();
    }, SESSION_TIMEOUT);
  }, [logout]);

  /* ================= AUTH CHECK ================= */

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("corporator_token");

      if (!token) {
        setLoading(false);
        return;
      }

      const response = await api.get("/api/corporator/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.data) {
        setCorporator(response.data.data);
      } else {
        setCorporator(response.data);
      }

      resetSessionTimer();
    } catch (error) {
      console.error("Auth check failed:", error);
      console.log("Status:", error?.response?.status);
      console.log("Response:", error?.response?.data);

      localStorage.removeItem("corporator_token");
      setCorporator(null);
    } finally {
      setLoading(false);
    }
  }, [resetSessionTimer]);

  /* ================= INITIAL AUTH CHECK ================= */

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /* ================= USER ACTIVITY TRACKING ================= */

  useEffect(() => {
    if (!corporator) return;

    const events = [
      "mousemove",
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      resetSessionTimer();
    };

    events.forEach((event) =>
      window.addEventListener(event, handleActivity)
    );

    resetSessionTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );

      clearTimeout(sessionTimerRef.current);
    };
  }, [corporator, resetSessionTimer]);

  /* ================= LOGIN ================= */

  const login = async (token) => {
    try {
      localStorage.setItem("corporator_token", token);

      const response = await api.get("/api/corporator/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.data) {
        setCorporator(response.data.data);
      } else {
        setCorporator(response.data);
      }

      resetSessionTimer();

      return response.data;
    } catch (error) {
      console.error("Login error:", error);
      console.log("Status:", error?.response?.status);
      console.log("Response:", error?.response?.data);

      localStorage.removeItem("corporator_token");

      throw error;
    }
  };

  /* ================= CONTEXT VALUE ================= */

  const value = {
    corporator,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;